import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = [
    "searchInput", "frame", "tabBtn", "tabBadge", "typesDropdown",
    "typeCheckbox", "typeToggleAll",
    "repmCheckbox", "repmToggleAll",
    "regionCheckbox", "regionToggleAll",
    "statusCheckbox", "statusToggleAll",
    "dateFrom", "dateTo", "datePresetBtn", "filterBadge", "clearFiltersRow",
    "exportButton",
    "editModal", "editPropertyNum", "editProjectStatus", "editRegion",
    "editRepm", "editAnalyst", "editPlanStatus",
    "editProposedFunds", "editBudgetedFunds", "editAppropriatedFunds",
    "editForecastCont", "editContracted", "editForecastClosing", "editAcquired",
    "editSubmitBtn", "editProposedRow", "editDatesRow"
  ]
  static values = {
    savedSortKey: { type: String, default: "priority" },
    savedSortOrder: { type: String, default: "asc" },
    savedRegions: { type: Array, default: [] },
    savedRepmFilters: { type: Array, default: [] },
    savedTab: { type: String, default: "all" },
    savedStatuses: { type: Array, default: [] },
    savedDateFrom: { type: String, default: "" },
    savedDateTo: { type: String, default: "" },
    savedDatePreset: { type: String, default: "" }
  }

  connect() {
    this._debounceTimer = null
    this._currentTab = this.savedTabValue || "all"
    this._currentSortKey = this.savedSortKeyValue || "priority"
    this._currentSortOrder = this.savedSortOrderValue || "asc"

    // Restore saved date range
    this._currentDatePreset = this.savedDatePresetValue || ""
    if (this._currentDatePreset) {
      this._applyPresetDates(this._currentDatePreset)
      this._highlightDatePreset(this._currentDatePreset)
    } else if (this.savedDateFromValue || this.savedDateToValue) {
      if (this.savedDateFromValue && this.hasDateFromTarget) this.dateFromTarget.value = this.savedDateFromValue
      if (this.savedDateToValue && this.hasDateToTarget) this.dateToTarget.value = this.savedDateToValue
      this._highlightDatePreset("")
    }

    this._updateFilterBadge()
  }

  // Tab switching
  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this._currentTab = tab

    // Update active tab styling
    this.tabBtnTargets.forEach((btn) => {
      if (btn.dataset.tab === tab) {
        btn.classList.add("bg-white", "text-brand", "font-bold")
        btn.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
      } else {
        btn.classList.remove("text-brand", "font-bold")
        btn.classList.add("bg-white", "text-text-muted", "font-medium", "hover:text-gray-700")
      }
    })

    this.tabBadgeTargets.forEach((badge) => {
      if (badge.dataset.tab === tab) {
        badge.classList.add("bg-brand/15", "text-brand")
        badge.classList.remove("bg-gray-200", "text-text-muted")
      } else {
        badge.classList.remove("bg-brand/15", "text-brand")
        badge.classList.add("bg-gray-200", "text-text-muted")
      }
    })

    // Toggle types dropdown visibility (only on "All" tab)
    if (this.hasTypesDropdownTarget) {
      this.typesDropdownTarget.style.display = tab === "all" ? "" : "none"
    }

    this._reload()
  }

  // Column sort
  sortByColumn(event) {
    const key = event.currentTarget.dataset.sortKey
    if (this._currentSortKey === key) {
      this._currentSortOrder = this._currentSortOrder === "asc" ? "desc" : "asc"
    } else {
      this._currentSortKey = key
      this._currentSortOrder = "asc"
    }

    // Save sort preferences
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch("/real_estate/save_sort_preferences", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: `sort_by=${encodeURIComponent(this._currentSortKey)}&sort_order=${encodeURIComponent(this._currentSortOrder)}`
    })

    this._reload()
  }

  // Debounced search
  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._reload(), 300)
  }

  // Types filter
  filterTypes() {
    const checkboxes = this.typeCheckboxTargets
    const allChecked = checkboxes.every((cb) => cb.checked)
    this.typeToggleAllTargets.forEach((toggle) => {
      toggle.checked = allChecked
      toggle.indeterminate = !allChecked && checkboxes.some((cb) => cb.checked)
    })
    this._reload()
  }

  toggleAllTypes(event) {
    const checked = event.currentTarget.checked
    this.typeCheckboxTargets.forEach((cb) => { cb.checked = checked })
    this.typeToggleAllTargets.forEach((toggle) => {
      toggle.checked = checked
      toggle.indeterminate = false
    })
    this._reload()
  }

  // REPM filter
  filterRepms() {
    const checkboxes = this.repmCheckboxTargets
    const allChecked = checkboxes.every((cb) => cb.checked)
    this.repmToggleAllTargets.forEach((toggle) => {
      toggle.checked = allChecked
      toggle.indeterminate = !allChecked && checkboxes.some((cb) => cb.checked)
    })
    this._reload()
  }

  toggleAllRepms(event) {
    const checked = event.currentTarget.checked
    this.repmCheckboxTargets.forEach((cb) => { cb.checked = checked })
    this.repmToggleAllTargets.forEach((toggle) => {
      toggle.checked = checked
      toggle.indeterminate = false
    })
    this._reload()
  }

  // Region filter
  filterRegions() {
    const checkboxes = this.regionCheckboxTargets
    const allChecked = checkboxes.every((cb) => cb.checked)
    this.regionToggleAllTargets.forEach((toggle) => {
      toggle.checked = allChecked
      toggle.indeterminate = !allChecked && checkboxes.some((cb) => cb.checked)
    })
    this._reload()
  }

  toggleAllRegions(event) {
    const checked = event.currentTarget.checked
    this.regionCheckboxTargets.forEach((cb) => { cb.checked = checked })
    this.regionToggleAllTargets.forEach((toggle) => {
      toggle.checked = checked
      toggle.indeterminate = false
    })
    this._reload()
  }

  // Status filter
  filterStatuses() {
    const checkboxes = this.statusCheckboxTargets
    const allChecked = checkboxes.every((cb) => cb.checked)
    this.statusToggleAllTargets.forEach((toggle) => {
      toggle.checked = allChecked
      toggle.indeterminate = !allChecked && checkboxes.some((cb) => cb.checked)
    })
    this._reload()
  }

  toggleAllStatuses(event) {
    const checked = event.currentTarget.checked
    this.statusCheckboxTargets.forEach((cb) => { cb.checked = checked })
    this.statusToggleAllTargets.forEach((toggle) => {
      toggle.checked = checked
      toggle.indeterminate = false
    })
    this._reload()
  }

  // Date range filter
  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    this._currentDatePreset = preset
    this._applyPresetDates(preset)
    this._highlightDatePreset(preset)
    this._reload()
  }

  filterDates() {
    this._currentDatePreset = ""
    this._highlightDatePreset("")
    this._reload()
  }

  // Clear all filters
  clearFilters() {
    // Reset all checkbox filters to checked
    const allCheckboxGroups = [
      [this.typeCheckboxTargets, this.typeToggleAllTargets],
      [this.repmCheckboxTargets, this.repmToggleAllTargets],
      [this.regionCheckboxTargets, this.regionToggleAllTargets],
      [this.statusCheckboxTargets, this.statusToggleAllTargets]
    ]
    allCheckboxGroups.forEach(([checkboxes, toggleAlls]) => {
      checkboxes.forEach((cb) => { cb.checked = true })
      toggleAlls.forEach((t) => { t.checked = true; t.indeterminate = false })
    })

    // Reset date range
    this._currentDatePreset = ""
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""
    this._highlightDatePreset("")

    this._reload()
  }

  _applyPresetDates(preset) {
    const today = new Date()
    let from = ""
    let to = ""

    if (preset === "30" || preset === "90" || preset === "365") {
      const days = parseInt(preset)
      const fromDate = new Date(today)
      fromDate.setDate(fromDate.getDate() - days)
      from = this._formatDate(fromDate)
      to = this._formatDate(today)
    } else if (preset === "ytd") {
      from = `${today.getFullYear()}-01-01`
      to = this._formatDate(today)
    }

    if (this.hasDateFromTarget) this.dateFromTarget.value = from
    if (this.hasDateToTarget) this.dateToTarget.value = to
  }

  _highlightDatePreset(activePreset) {
    if (!this.hasDatePresetBtnTarget) return
    this.datePresetBtnTargets.forEach((btn) => {
      const isActive = btn.dataset.preset === activePreset
      if (isActive) {
        btn.classList.add("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
        btn.classList.remove("text-gray-500", "hover:bg-gray-50", "border-gray-200")
      } else {
        btn.classList.remove("bg-brand/10", "text-brand", "font-medium", "border-brand/30")
        btn.classList.add("text-gray-500", "hover:bg-gray-50", "border-gray-200")
      }
    })
  }

  _formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  // Row click navigation
  navigateToStep(event) {
    // Don't navigate if clicking on a link, button, or dropdown menu inside the row
    if (event.target.closest("a, button, [data-dropdown-target='menu']")) return
    const url = event.currentTarget.dataset.url
    if (url) Turbo.visit(url)
  }

  // Edit modal
  openEditModal(event) {
    event.stopPropagation()
    const row = event.target.closest("tr")
    if (!row) return

    this._editingProjectId = row.dataset.projectId

    // Populate modal fields from row data attributes
    if (this.hasEditPropertyNumTarget) this.editPropertyNumTarget.value = row.dataset.propertyNum || ""
    if (this.hasEditProjectStatusTarget) this.editProjectStatusTarget.value = row.dataset.projectStatus || ""
    if (this.hasEditRegionTarget) this.editRegionTarget.value = row.dataset.regionId || ""
    if (this.hasEditRepmTarget) this.editRepmTarget.value = row.dataset.repmEmail || ""
    if (this.hasEditAnalystTarget) this.editAnalystTarget.value = row.dataset.analystEmail || ""
    if (this.hasEditPlanStatusTarget) this.editPlanStatusTarget.value = row.dataset.planStatus || ""

    const proposedFunds = parseFloat(row.dataset.proposedFunds)
    if (this.hasEditProposedFundsTarget) this.editProposedFundsTarget.value = proposedFunds > 0 ? proposedFunds : ""
    const budgetedFunds = parseFloat(row.dataset.budgetedFunds)
    if (this.hasEditBudgetedFundsTarget) this.editBudgetedFundsTarget.value = budgetedFunds > 0 ? budgetedFunds : ""
    const appropriatedFunds = parseFloat(row.dataset.appropriatedFunds)
    if (this.hasEditAppropriatedFundsTarget) this.editAppropriatedFundsTarget.value = appropriatedFunds > 0 ? appropriatedFunds : ""

    this._setDatepicker(this.hasEditForecastContTarget && this.editForecastContTarget, row.dataset.forecastCont)
    this._setDatepicker(this.hasEditContractedTarget && this.editContractedTarget, row.dataset.contracted)
    this._setDatepicker(this.hasEditForecastClosingTarget && this.editForecastClosingTarget, row.dataset.forecastClosing)
    this._setDatepicker(this.hasEditAcquiredTarget && this.editAcquiredTarget, row.dataset.acquired)

    // Toggle fields based on current tab:
    // "all" tab: show Proposed, hide dates
    // other tabs: hide Proposed, show dates
    const isAllTab = this._currentTab === "all"
    if (this.hasEditProposedRowTarget) this.editProposedRowTarget.classList.toggle("hidden", !isAllTab)
    if (this.hasEditDatesRowTarget) this.editDatesRowTarget.classList.toggle("hidden", isAllTab)

    // Open modal
    this.editModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this._onEditModalKeydown = (e) => { if (e.key === "Escape") this.closeEditModal() }
    document.addEventListener("keydown", this._onEditModalKeydown)
  }

  closeEditModal() {
    this.editModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    if (this._onEditModalKeydown) {
      document.removeEventListener("keydown", this._onEditModalKeydown)
    }
    this._editingProjectId = null
  }

  closeEditModalOnBackdrop(event) {
    if (event.target === this.editModalTarget) {
      this.closeEditModal()
    }
  }

  updateProject() {
    if (!this._editingProjectId) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const btn = this.hasEditSubmitBtnTarget ? this.editSubmitBtnTarget : null

    // Disable button while saving
    if (btn) {
      btn.disabled = true
      btn.textContent = "Saving..."
    }

    const body = new URLSearchParams({
      property_num: this.hasEditPropertyNumTarget ? this.editPropertyNumTarget.value : "",
      project_status: this.hasEditProjectStatusTarget ? this.editProjectStatusTarget.value : "",
      region_id: this.hasEditRegionTarget ? this.editRegionTarget.value : "",
      repm_email: this.hasEditRepmTarget ? this.editRepmTarget.value : "",
      analyst_email: this.hasEditAnalystTarget ? this.editAnalystTarget.value : "",
      plan_status: this.hasEditPlanStatusTarget ? this.editPlanStatusTarget.value : "",
      proposed_funds: this.hasEditProposedFundsTarget ? this.editProposedFundsTarget.value : "",
      budgeted_funds: this.hasEditBudgetedFundsTarget ? this.editBudgetedFundsTarget.value : "",
      appropriated_funds: this.hasEditAppropriatedFundsTarget ? this.editAppropriatedFundsTarget.value : "",
      forecast_cont: this.hasEditForecastContTarget ? this.editForecastContTarget.value : "",
      contracted: this.hasEditContractedTarget ? this.editContractedTarget.value : "",
      forecast_closing: this.hasEditForecastClosingTarget ? this.editForecastClosingTarget.value : "",
      acquired: this.hasEditAcquiredTarget ? this.editAcquiredTarget.value : ""
    })

    fetch(`/real_estate/update_project_from_modal/${this._editingProjectId}`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: body
    })
      .then((response) => {
        if (response.ok) {
          this.closeEditModal()
          this._reload()
          this._showToast("Project updated successfully")
        } else {
          this._showToast("Failed to update project. Please try again.", "error")
        }
      })
      .catch(() => {
        this._showToast("Failed to update project. Please try again.", "error")
      })
      .finally(() => {
        if (btn) {
          btn.disabled = false
          btn.textContent = "Update"
        }
      })
  }

  // Export Excel
  // Migration Export pop-up: lets the requester confirm/override the email
  // address the download link is sent to (Phelipe, 2026-07-21).
  openMigrationExport() {
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "re-migration-export" } }))
  }

  exportExcel() {
    // Close dropdown
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")

    this._showExportSpinner()

    const url = this._buildExportUrl()

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error("Export failed")
        return response.blob()
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = blobUrl
        const today = new Date().toISOString().split("T")[0]
        a.download = `real_estate_export_${today}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(blobUrl)
        this._showToast("Export downloaded successfully")
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => this._hideExportSpinner())
  }

  _showExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _hideExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  _reload() {
    this._updateFilterBadge()
    const frame = this.frameTarget
    frame.src = this._buildUrl()
  }

  _buildUrl() {
    const url = new URL("/real_estate/list_projects", window.location.origin)
    url.searchParams.set("page", "1")
    url.searchParams.set("format", "html")
    url.searchParams.set("tab", this._currentTab || "all")
    url.searchParams.set("sort_by", this._currentSortKey || "priority")
    url.searchParams.set("sort_order", this._currentSortOrder || "asc")

    const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    if (q) url.searchParams.set("q", q)

    // Types filter (only on All tab)
    if (this._currentTab === "all" && this.hasTypeCheckboxTarget) {
      const selected = this.typeCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.typeCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((t) => url.searchParams.append("types[]", t))
      }
    }

    // REPM filter (always send so server can persist/clear)
    if (this.hasRepmCheckboxTarget) {
      const selected = this.repmCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.repmCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((r) => url.searchParams.append("repms[]", r))
      } else {
        url.searchParams.set("repms_all", "1")
      }
    }

    // Region filter (always send so server can persist/clear)
    if (this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.regionCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((r) => url.searchParams.append("regions[]", r))
      } else {
        url.searchParams.set("regions_all", "1")
      }
    }

    // Status filter (always send so server can persist/clear)
    if (this.hasStatusCheckboxTarget) {
      const selected = this.statusCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.statusCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((s) => url.searchParams.append("statuses[]", s))
      } else {
        url.searchParams.set("statuses_all", "1")
      }
    }

    // Date range filter (always send so server can persist/clear)
    const dateFrom = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    const dateTo = this.hasDateToTarget ? this.dateToTarget.value : ""
    url.searchParams.set("date_from", dateFrom)
    url.searchParams.set("date_to", dateTo)
    url.searchParams.set("date_preset", this._currentDatePreset || "")

    return url.toString()
  }

  _buildExportUrl() {
    const url = new URL("/real_estate/real_estate_export", window.location.origin)
    url.searchParams.set("tab", this._currentTab || "all")
    url.searchParams.set("sort_by", this._currentSortKey || "priority")
    url.searchParams.set("sort_order", this._currentSortOrder || "asc")

    const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    if (q) url.searchParams.set("q", q)

    // Types filter (only on All tab)
    if (this._currentTab === "all" && this.hasTypeCheckboxTarget) {
      const selected = this.typeCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.typeCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((t) => url.searchParams.append("types[]", t))
      }
    }

    if (this.hasRepmCheckboxTarget) {
      const selected = this.repmCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.repmCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((r) => url.searchParams.append("repms[]", r))
      }
    }

    // Region filter
    if (this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.regionCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((r) => url.searchParams.append("regions[]", r))
      }
    }

    // Status filter
    if (this.hasStatusCheckboxTarget) {
      const selected = this.statusCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.statusCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((s) => url.searchParams.append("statuses[]", s))
      }
    }

    // Date range filter
    const dateFrom = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    const dateTo = this.hasDateToTarget ? this.dateToTarget.value : ""
    if (dateFrom) url.searchParams.set("date_from", dateFrom)
    if (dateTo) url.searchParams.set("date_to", dateTo)

    return url.toString()
  }

  _updateFilterBadge() {
    if (!this.hasFilterBadgeTarget) return

    let count = 0
    // Count checkbox-based filters that are not "all selected"
    const groups = [
      this.typeCheckboxTargets,
      this.repmCheckboxTargets,
      this.regionCheckboxTargets,
      this.statusCheckboxTargets
    ]
    groups.forEach((checkboxes) => {
      if (!checkboxes.length) return
      const selected = checkboxes.filter((cb) => cb.checked).length
      if (selected < checkboxes.length) count++
    })

    // Count date filter
    const dateFrom = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    const dateTo = this.hasDateToTarget ? this.dateToTarget.value : ""
    if (dateFrom || dateTo) count++

    const badge = this.filterBadgeTarget
    if (count > 0) {
      badge.textContent = count
      badge.classList.remove("hidden")
      badge.classList.add("inline-flex")
    } else {
      badge.classList.add("hidden")
      badge.classList.remove("inline-flex")
    }

    // Show/hide clear row
    if (this.hasClearFiltersRowTarget) {
      this.clearFiltersRowTarget.classList.toggle("hidden", count === 0)
    }
  }

  _setDatepicker(el, value) {
    if (!el) return
    const fp = el._flatpickr
    if (fp) {
      fp.setDate(value || "", false)
    } else {
      el.value = value || ""
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
